/**
 * Call registry — owns per-call XState actors + secondary indexes.
 *
 * The registry is the single source of truth for active call state.
 * Consumers (hooks, UI) subscribe and read via getters; they never mutate
 * the underlying records directly — all writes go through `update()` or
 * the XState actor via `send()`.
 */

import type { CallId, CallRecord, ClientCallId } from './types';
import { spawnCallActor, type CallActor, type CallMachineEvent } from './machine';

export interface RegistryEntry {
  record: CallRecord;
  actor: CallActor;
}

type Listener = () => void;

const entries = new Map<ClientCallId, RegistryEntry>();
const byTelnyxId = new Map<string, ClientCallId>();
const byCallId = new Map<CallId, ClientCallId>();
const listeners = new Set<Listener>();

function notify(): void {
  listeners.forEach((l) => {
    try {
      l();
    } catch (err) {
      console.error('[registry] listener error', err);
    }
  });
}

export function add(record: CallRecord): RegistryEntry {
  const actor = spawnCallActor(record);

  actor.subscribe((snapshot) => {
    const entry = entries.get(record.clientCallId);
    if (!entry) return;
    // Alleen de machine-eigen lifecycle-velden mergen. De volledige context
    // spreiden clobberde verrijkte velden (callId, customer, brand) terug
    // naar hun waarde van vóór registry.update().
    const { state, answeredAt, endedAt, recoveredFromCallId } = snapshot.context;
    entry.record = { ...entry.record, state, answeredAt, endedAt, recoveredFromCallId };
    notify();
    // Centrale opruiming: de machine is final (ended) → entry weg na een
    // korte UI-gratie. Vervangt de losse setTimeout(remove, 200)-callsites
    // en ruimt óók de ending→ended-timeout-route op (geen zombie-records).
    if (snapshot.status === 'done') {
      setTimeout(() => remove(record.clientCallId), 200);
    }
  });

  const entry: RegistryEntry = { record, actor };
  entries.set(record.clientCallId, entry);
  if (record.telnyxCallId) byTelnyxId.set(record.telnyxCallId, record.clientCallId);
  if (record.callId) byCallId.set(record.callId, record.clientCallId);

  notify();
  return entry;
}

export function update(clientCallId: ClientCallId, patch: Partial<CallRecord>): void {
  const entry = entries.get(clientCallId);
  if (!entry) return;

  if (patch.telnyxCallId && patch.telnyxCallId !== entry.record.telnyxCallId) {
    if (entry.record.telnyxCallId) byTelnyxId.delete(entry.record.telnyxCallId);
    byTelnyxId.set(patch.telnyxCallId, clientCallId);
  }
  if (patch.callId && patch.callId !== entry.record.callId) {
    if (entry.record.callId) byCallId.delete(entry.record.callId);
    byCallId.set(patch.callId, clientCallId);
  }

  entry.record = { ...entry.record, ...patch };
  notify();
}

export function send(clientCallId: ClientCallId, event: CallMachineEvent): void {
  const entry = entries.get(clientCallId);
  if (!entry) return;
  entry.actor.send(event);
}

export function remove(clientCallId: ClientCallId): void {
  const entry = entries.get(clientCallId);
  if (!entry) return;

  try {
    entry.actor.stop();
  } catch {
    /* ignore */
  }

  if (entry.record.telnyxCallId) byTelnyxId.delete(entry.record.telnyxCallId);
  if (entry.record.callId) byCallId.delete(entry.record.callId);
  entries.delete(clientCallId);
  notify();
}

export function get(clientCallId: ClientCallId): RegistryEntry | undefined {
  return entries.get(clientCallId);
}

export function getByTelnyxId(telnyxId: string): ClientCallId | undefined {
  return byTelnyxId.get(telnyxId);
}

export function getByCallId(callId: CallId): ClientCallId | undefined {
  return byCallId.get(callId);
}

export function all(): RegistryEntry[] {
  return Array.from(entries.values());
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Merge a recovered call into an existing actor:
 * 1. Find the old entry by its Telnyx call id.
 * 2. Send RECOVERED on the new actor (which already exists at this point).
 * 3. Remove the old entry from the registry.
 */
export function mergeRecovered(
  newClientCallId: ClientCallId,
  oldTelnyxCallId: string,
): void {
  const oldClientCallId = byTelnyxId.get(oldTelnyxCallId);
  if (!oldClientCallId || oldClientCallId === newClientCallId) return;

  send(newClientCallId, { type: 'RECOVERED', fromId: oldTelnyxCallId });
  remove(oldClientCallId);
}
